import { BaseService } from "../BaseService";
import { AuthService } from "../auth/auth.service";
import { environment } from '../../../../src/environments/environment';
import { HttpClient } from "@angular/common/http";
import { Router } from '@angular/router';
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { ApiResult } from "../../models/ApiResult";
import { BookingModel } from "../../models/api/BookingModel";
import { BookingFindResultModel } from "../../models/api/BookingFindResultModel";
import { Grid_BookingModel } from "../../models/api/Grid_BookingModel";
import { Grid_BookingFindResultModel } from "../../models/api/Grid_BookingFindResultModel";

export abstract class BookingBaseService extends BaseService {

    private urlPart: string = 'Booking';

    constructor(private http: HttpClient, private router: Router, private auth: AuthService) {
        super(http, router, auth);
        this.apiSegmentUrl = this.urlPart;
    };

    /// <summary>
    /// FIND: Retrieves a list of Booking records from the database
    /// /Booking
    /// </summary>
    public findBooking(page: number = 0, size: number = 0, orderBy: string = "", orderDirection: string = "ASC", filter: string = "", requestedtherapistid: number = 0, assignedtherapistid: number = 0, contactmethodid: number = 0, bookingstatusid: number = 0): Observable<BookingFindResultModel>
    {
        // Find Booking Records

        const url = `${super.getBaseUrl()}?page=${page}&size=${size}&orderBy=${orderBy}&orderDirection=${orderDirection}&filter=${filter}&requestedtherapistid=${requestedtherapistid}&assignedtherapistid=${assignedtherapistid}&contactmethodid=${contactmethodid}&bookingstatusid=${bookingstatusid}`;

        if (!environment.production)
            console.log("Find Booking: Calling " + url);

        return this.http.get<BookingFindResultModel>(url)
            .pipe(
                map(next => {
                    let o: BookingFindResultModel = Object.assign(new BookingFindResultModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public findBookingGrid(page: number = 0, size: number = 0, orderBy: string = "", orderDirection: string = "ASC", filter: string = "", bookingid: number = 0, requestedtherapistid: number = 0, assignedtherapistid: number = 0, contactmethodid: number = 0, bookingstatusid: number = 0): Observable<Grid_BookingFindResultModel> {
      // Find Grid_Booking Records

      const url = `${super.getBaseUrl()}/grid?page=${page}&size=${size}&orderBy=${orderBy}&orderDirection=${orderDirection}&filter=${filter}&bookingid=${bookingid}&requestedtherapistid=${requestedtherapistid}&assignedtherapistid=${assignedtherapistid}&contactmethodid=${contactmethodid}&bookingstatusid=${bookingstatusid}`;

      if (!environment.production)
        console.log("Find Grid_Booking: Calling " + url);

      return this.http.get<Grid_BookingFindResultModel>(url)
        .pipe(
          map(next => {
            let o: Grid_BookingFindResultModel = Object.assign(new Grid_BookingFindResultModel(), next);
            return o;
          }),
          retryWhen(errors =>
            errors.pipe(
              concatMap((error, count) => {
                if (count > this.RETRY_CEILING) return throwError(error);
                return this.tokenCheckAndRefresh(error);
              }),
              delay(1000),
            ),
          ),
          catchError(this.errorHandler)
        );
    }

    /// <summary>
    /// GET: Retrieves a single Grid_Booking record from the database
    /// /Grid_Booking/{bookingid}
    /// </summary>
    /// <param name="bookingid"></param>
    /// <returns></returns>
    public getBookingGridRecord(bookingid: number): Observable<Grid_BookingModel> {

      // Get Single Grid_Booking Record

      const url = `${super.getBaseUrl()}/grid/${bookingid}`;

      if (!environment.production)
        console.log("Get Booking: Calling " + url);

      return this.http.get<Grid_BookingModel>(url)
        .pipe(
          map(next => {
            let o: Grid_BookingModel = Object.assign(new Grid_BookingModel(), next);
            return o;
          }),
          retryWhen(errors =>
            errors.pipe(
              concatMap((error, count) => {
                if (count > this.RETRY_CEILING) return throwError(error);
                return this.tokenCheckAndRefresh(error);
              }),
              delay(1000),
            ),
          ),
          catchError(this.errorHandler)
        );
    }

    /// <summary>
    /// GET: Retrieves a single Booking record from the database
    /// /Booking/{bookingref}
    /// </summary>
    /// <param name="bookingid"></param>
    /// <returns></returns>
    public getBooking(bookingref: string): Observable<BookingModel>
    {

        // Get Single Booking Record

        // TODO: Get Single Booking Record

        const url = `${super.getBaseUrl()}/${bookingref}`;

        if (!environment.production)
            console.log("Get Booking: Calling " + url);

        return this.http.get<BookingModel>(url)
            .pipe(
                map(next => {
                    let o: BookingModel = Object.assign(new BookingModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }


    /// <summary>
    /// GET: Retrieves a single Booking record from the database
    /// /Booking/{bookingref}
    /// </summary>
    /// <param name="bookingid"></param>
    /// <returns></returns>
    public getBookingById(bookingid: number): Observable<BookingModel>
    {

        // Get Single Booking Record

        // TODO: Get Single Booking Record

        const url = `${super.getBaseUrl()}/byid/${bookingid}`;

        if (!environment.production)
            console.log("Get Booking by id: Calling " + url);

        return this.http.get<BookingModel>(url)
            .pipe(
                map(next => {
                    let o: BookingModel = Object.assign(new BookingModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }


    /// <summary>
    /// GET: Retrieves all Booking records from the database
    /// /Booking/
    /// </summary>
    /// <returns></returns>
    public getAllBookings(): Observable<BookingModel[]>
    {

        // Get Single Booking Record

        // TODO: Get Single Booking Record

        const url = `${super.getBaseUrl()}`;

        if (!environment.production)
            console.log("Get All Bookings: Calling " + url);

        return this.http.get<any>(url).pipe(
            map(response => {
                const data = Array.isArray(response.data)
                    ? response.data
                    : Array.from(response.data as any);

                return data.map((item: any) => Object.assign(new BookingModel(), item));
            }),
            retryWhen(errors =>
                errors.pipe(
                    concatMap((error, count) => {
                        if (count > this.RETRY_CEILING) return throwError(error);
                        return this.tokenCheckAndRefresh(error);
                    }),
                    delay(1000),
                )
            ),
            catchError(this.errorHandler)
        );
    }




    /// <summary>
    /// POST: Adds a new Booking record to the database
    /// /Booking/{bookingid}
    /// </summary>
    /// <param name="bookingid"></param>
    /// <returns></returns>
    public createBooking(newRecord: BookingModel): Observable<BookingModel>
    {
        // Add Single Booking Record

        const url = `${super.getBaseUrl()}`;

        if (!environment.production)
            console.log("Create Booking:Calling " + url, newRecord);

        return this.http.post<BookingModel>(url, newRecord)
            .pipe(
                map(next => {
                    let o: BookingModel = Object.assign(new BookingModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    /// <summary>
    /// PUT: Updates an existing Booking record in the database
    /// /Booking/{bookingid}
    /// </summary>
    /// <param name="bookingid"></param>
    /// <returns></returns>
    public updateBooking(bookingid: number, updatedRecord: BookingModel): Observable<BookingModel>
    {
        // Update Single Booking Record

console.log(JSON.stringify(updatedRecord));

        const url = `${super.getBaseUrl()}/${bookingid}`;

        if (!environment.production)
            console.log("Update Booking: Calling " + url);

        return this.http.put<BookingModel>(url, updatedRecord)
            .pipe(
                map(next => {
                    let o: BookingModel = Object.assign(new BookingModel(), next);
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public updateBookingRecord(updatedRecord: BookingModel): Observable<BookingModel>
    {
        return this.updateBooking(updatedRecord.bookingID, updatedRecord);
    }

    /// <summary>
    /// DELETE: Deletes an existing Booking record from the database
    /// /Booking/{bookingid}
    /// </summary>
    /// <param name="bookingid"></param>
    /// <returns></returns>
    public deleteBooking(bookingid: number): Observable<ApiResult>
    {
        // Delete Single Booking Record

        const url = `${super.getBaseUrl()}/${bookingid}`;

        if (!environment.production)
            console.log("Delete Booking: Calling " + url);

        return this.http.delete<ApiResult>(url)
            .pipe(
                map(next => {
                    let o: ApiResult = new ApiResult();
                    o.success = true;
                    return o;
                }),
                retryWhen(errors =>
                    errors.pipe(
                        concatMap((error, count) => {
                            if (count > this.RETRY_CEILING) return throwError(error);
                            return this.tokenCheckAndRefresh(error);
                        }),
                        delay(1000),
                    ),
                ),
                catchError(this.errorHandler)
            );
    }

    public deleteBookingRecord(deleteRecord: BookingModel): Observable<ApiResult>
    {
        // Delete Single Booking Record
        return this.deleteBooking(deleteRecord.bookingID);
    }

}
